import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Sparkles, ArrowLeft, LogOut, Home, User, Calendar, 
  CheckCircle2, Clock, AlertCircle, RefreshCw
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HousekeepingDashboard() {
  const navigate = useNavigate();
  const [cabins, setCabins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    // Check auth
    if (!sessionStorage.getItem("housekeepingAuth")) {
      navigate("/housekeeping");
      return;
    }
    loadCabins();
  }, [navigate]);

  const loadCabins = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/housekeeping/cabins`);
      if (res.ok) {
        const data = await res.json();
        setCabins(data);
      } else {
        toast.error("Failed to load cabin data");
      }
    } catch (error) {
      console.error("Error loading cabins:", error);
      toast.error("Error loading cabin data");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (cabinId, newStatus) => {
    try {
      setUpdating(cabinId);
      const res = await fetch(`${API_URL}/api/housekeeping/cabins/${cabinId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cleaning_status: newStatus })
      });
      
      if (res.ok) {
        toast.success(`Cabin marked as ${newStatus.replace("_", " ")}`);
        loadCabins(); // Refresh data
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error updating status");
    } finally {
      setUpdating(null);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("housekeepingAuth");
    navigate("/housekeeping");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "cleaned":
        return <Badge className="bg-green-600 text-white"><CheckCircle2 className="w-3 h-3 mr-1" /> Cleaned</Badge>;
      case "in_progress":
        return <Badge className="bg-blue-600 text-white"><Clock className="w-3 h-3 mr-1" /> In Progress</Badge>;
      case "needs_cleaning":
        return <Badge className="bg-red-600 text-white"><AlertCircle className="w-3 h-3 mr-1" /> Needs Cleaning</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div className="min-h-screen bg-stone-300">
      {/* Header */}
      <header className="bg-stone-500 text-white shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold">Housekeeping</h1>
              <p className="text-stone-200 text-sm">Bird Brain Bungalows</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={loadCabins}
              className="text-white hover:bg-stone-600"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="text-white hover:bg-stone-600"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h2 className="font-display text-2xl font-bold text-stone-800 mb-2">Cabin Status</h2>
          <p className="text-stone-600">Mark cabins as cleaned when ready for guests</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="bg-stone-100 border-stone-300 animate-pulse">
                <CardContent className="p-6">
                  <div className="h-6 bg-stone-300 rounded w-1/3 mb-4"></div>
                  <div className="h-4 bg-stone-300 rounded w-2/3 mb-2"></div>
                  <div className="h-4 bg-stone-300 rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {cabins.map((cabin) => (
              <Card 
                key={cabin.cabin_id} 
                className={`bg-stone-100 border-2 transition-colors ${
                  cabin.cleaning_status === "needs_cleaning" ? "border-red-400" :
                  cabin.cleaning_status === "in_progress" ? "border-blue-400" :
                  "border-stone-300"
                }`}
                data-testid={`cabin-card-${cabin.cabin_id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      <Home className="w-5 h-5 text-primary" />
                      <CardTitle className="text-lg text-stone-800">{cabin.cabin_name}</CardTitle>
                    </div>
                    {getStatusBadge(cabin.cleaning_status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {cabin.guest ? (
                    <div className="bg-stone-200/50 rounded-lg p-3 space-y-2">
                      <div className="flex items-center gap-2 text-stone-700">
                        <User className="w-4 h-4" />
                        <span className="font-medium">{cabin.guest.guest_name}</span>
                        <span className="text-stone-500">({cabin.guest.guest_count} guests)</span>
                      </div>
                      <div className="flex items-center gap-2 text-stone-600 text-sm">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {formatDate(cabin.guest.arrival_date)} - {formatDate(cabin.guest.departure_date)}
                        </span>
                      </div>
                      {cabin.guest.checkout_time && (
                        <p className="text-sm text-stone-600">
                          Checkout: {cabin.guest.checkout_time}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-stone-200/50 rounded-lg p-3 text-center text-stone-500">
                      No upcoming reservation
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2">
                    {cabin.cleaning_status !== "in_progress" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateStatus(cabin.cabin_id, "in_progress")}
                        disabled={updating === cabin.cabin_id}
                        className="flex-1 border-blue-400 text-blue-600 hover:bg-blue-50"
                        data-testid={`start-cleaning-${cabin.cabin_id}`}
                      >
                        <Clock className="w-4 h-4 mr-1" />
                        Start Cleaning
                      </Button>
                    )}
                    {cabin.cleaning_status !== "cleaned" && (
                      <Button
                        size="sm"
                        onClick={() => updateStatus(cabin.cabin_id, "cleaned")}
                        disabled={updating === cabin.cabin_id}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`mark-cleaned-${cabin.cabin_id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Mark Cleaned
                      </Button>
                    )}
                    {cabin.cleaning_status === "cleaned" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateStatus(cabin.cabin_id, "needs_cleaning")}
                        disabled={updating === cabin.cabin_id}
                        className="flex-1 border-red-400 text-red-600 hover:bg-red-50"
                        data-testid={`needs-cleaning-${cabin.cabin_id}`}
                      >
                        <AlertCircle className="w-4 h-4 mr-1" />
                        Needs Cleaning
                      </Button>
                    )}
                  </div>

                  {cabin.last_cleaned && (
                    <p className="text-xs text-stone-500 text-center">
                      Last cleaned: {new Date(cabin.last_cleaned).toLocaleString()}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!loading && cabins.length === 0 && (
          <Card className="bg-stone-100 border-stone-300">
            <CardContent className="p-8 text-center text-stone-500">
              No cabin data available
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
